/**
 * TerrainEditSyncModule — ALWAYS-ON mirror of the current world's Edit Mode
 * terrain strokes (terrain_edit rows) into the client stamp registry. Every
 * visitor renders the owner's sculpting and paint — not just the editor.
 *
 * · World-scoped lazy subscription (re-subscribed on every world hop; Law 3:
 *   no unscoped global subs at account-ready).
 * · Row storms coalesce behind a 120 ms trailing debounce (Law 4 — a brush
 *   drag is dozens of rows/s), then ONE registry rebuild + ONE invalidation
 *   carrying only the discs that changed (the chunk streamer rebuilds just
 *   the touched chunks; a personal world rebuilds its heightfield).
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { setTerrainEdits, terrainEdits, type TerrainEditStamp } from '../lib/terrain_edits.js';

const DEBOUNCE_MS = 120;

export class TerrainEditSyncModule implements IModule {
  readonly name = 'TerrainEditSync';

  private sub: { unsubscribe(): void } | null = null;
  private subWorld: bigint | null = null;
  private timer: number | null = null;
  private bound = false;

  init(_app: App): void {
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:world-cleared', () => { setTerrainEdits([]); });
    bus.on('atlas:account-ready', () => this.bind());
  }

  private bind(): void {
    const conn = atlas.conn;
    if (!conn || this.bound) return;
    this.bound = true;
    const tbl = (conn.db as unknown as { terrainEdit?: {
      onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void;
    } }).terrainEdit;
    if (!tbl) return; // pre-publish server
    const ping = (): void => this.schedule();
    tbl.onInsert(ping); tbl.onUpdate(ping); tbl.onDelete(ping);
    this.onWorld();
  }

  /** Re-scope the subscription to the world we now stand in. */
  private onWorld(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    if (this.subWorld === wid && this.sub) { this.schedule(); return; }
    const prev = this.sub;
    try {
      // subscribe-new-then-unsubscribe-old — the row cache never gaps.
      this.sub = conn.subscriptionBuilder()
        .onApplied(() => { if (prev) prev.unsubscribe(); this.schedule(); })
        .subscribe([`SELECT * FROM terrain_edit WHERE world_id = ${wid}`]);
      this.subWorld = wid; // latch AFTER success — a throw used to pin subWorld to a subscription that never happened
    } catch { /* pre-publish server — terrain edits are simply absent */ }
  }

  private schedule(): void {
    if (this.timer !== null) return;
    this.timer = window.setTimeout(() => { this.timer = null; this.rebuild(); }, DEBOUNCE_MS);
  }

  private rebuild(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const before = new Map<number, TerrainEditStamp>();
    for (const s of terrainEdits()) before.set(s.id, s);
    const next: TerrainEditStamp[] = [];
    try {
      for (const e of conn.db.terrainEdit.iter()) {
        if (e.worldId !== wid) continue;
        next.push({ id: Number(e.editId), kind: e.kind as TerrainEditStamp['kind'], x: e.x, z: e.z, r: e.radius, v: e.value, layer: e.layer });
      }
    } catch { return; /* row cache mid-churn — the next ping retries */ }
    // Which discs changed? (added or removed) — only those chunks rebuild.
    const changed: Array<{ x: number; z: number; r: number }> = [];
    const after = new Set<number>();
    for (const s of next) { after.add(s.id); if (!before.has(s.id)) changed.push({ x: s.x, z: s.z, r: s.r }); }
    for (const [id, s] of before) if (!after.has(id)) changed.push({ x: s.x, z: s.z, r: s.r });
    setTerrainEdits(next);
    if (changed.length > 0) bus.emit('atlas:terrain-edits-changed', { stamps: changed });
  }

  dispose(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.sub?.unsubscribe();
  }
}
